import { create } from "zustand";

/**
 * Unread chat tabs.
 *
 * Deliberately device-local: "I have not read this yet" is a fact about the
 * person sitting at this machine, not about the conversation, and syncing it
 * would make one teammate's triage clear another's dot. It persists across
 * reloads so marking a tab unread survives a refresh, and it is bounded so a
 * long-lived install cannot grow it without limit.
 */

const STORAGE_KEY = "rivets.unread-chats.v1";
/** A runaway set would be a slow leak in localStorage; oldest marks fall off. */
const MAX_UNREAD = 500;

function load(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? "[]") as unknown;
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
  } catch {
    return [];
  }
}

function save(ids: string[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch { /* Private mode or a full quota: the in-memory set still works. */ }
}

type UnreadChatsState = {
  /** Insertion-ordered, oldest first, so trimming drops the stalest mark. */
  unread: string[];
  mark: (chatId: string) => void;
  clear: (chatId: string) => void;
  reset: () => void;
};

export const useUnreadChats = create<UnreadChatsState>((set) => ({
  unread: load(),
  mark: (chatId) => set((state) => {
    if (state.unread.includes(chatId)) return state;
    const unread = [...state.unread, chatId].slice(-MAX_UNREAD);
    save(unread);
    return { unread };
  }),
  clear: (chatId) => set((state) => {
    if (!state.unread.includes(chatId)) return state;
    const unread = state.unread.filter((id) => id !== chatId);
    save(unread);
    return { unread };
  }),
  reset: () => {
    save([]);
    return set({ unread: [] });
  },
}));

/** Where selection goes when the tab you just marked unread is the open one:
 * the neighbour after it, else the one before, else nowhere. */
export function neighbourTabId<T extends { id: string }>(tabs: readonly T[], id: string): string | undefined {
  const index = tabs.findIndex((tab) => tab.id === id);
  if (index < 0) return undefined;
  return tabs[index + 1]?.id ?? tabs[index - 1]?.id;
}
